// Sugalarm — shared History-chart marker glyphs (cycle 44).
//
// Single source of truth for the five shapes the History chart draws:
// the per-reading trend dot, the carbs triangle, the insulin droplet,
// the "other event" dot, and the alert ring. Before this cycle these
// lived inline in HistoryChart.tsx with no legend; HistoryLegend.tsx
// now explains them and MUST draw the exact same primitives, so both
// the chart and the legend call `MarkerGlyph` here. If a future cycle
// restyles a glyph it changes in one place and the legend can't drift.
//
// Geometry is anchored on the glyph CENTER (cx, cy):
//   reading  → filled circle r 2.4
//   carbs    → triangle, apex (cx, cy-4), base (cx±5, cy+4)
//   insulin  → filled circle r 4
//   other    → filled circle r 3
//   alert    → filled circle r 5 + var(--bg) halo stroke 1.5
// These reproduce HistoryChart's pre-cycle-44 output pixel-for-pixel:
// the chart passes cx = x, cy = (yBase - 4) for treatment markers (so
// the carbs apex lands at yBase-8 and the base at yBase, exactly as
// before), cx/cy = the dot/alert position for readings/alerts.

import { Fragment } from "react";

import type { Treatment } from "../../lib/types";

export type MarkerKind = "reading" | "carbs" | "insulin" | "other" | "alert";

/** Chart treatment-marker color, by event_type. Moved here from
 *  HistoryChart so the legend pulls the identical color and the two
 *  surfaces can't disagree. */
export function treatmentColor(kind: Treatment["event_type"]): string {
  switch (kind) {
    case "carbs":
      return "var(--zone-warn)";
    case "insulin":
      return "var(--accent)";
    case "bg_check":
      return "var(--ink-2)";
    case "exercise":
      return "var(--zone-ok)";
    case "note":
    default:
      return "var(--ink-3)";
  }
}

/** Map an event_type onto the glyph SHAPE the chart draws for it.
 *  carbs → triangle, insulin → droplet, everything else → small dot
 *  (the legend's "Other event" row covers them collectively). */
export function glyphKindForEvent(kind: Treatment["event_type"]): MarkerKind {
  if (kind === "carbs") return "carbs";
  if (kind === "insulin") return "insulin";
  return "other";
}

interface Props {
  kind: MarkerKind;
  /** Fill color. The chart passes zoneColor()/treatmentColor(); the
   *  legend passes the row's representative CSS var. Required so the
   *  glyph never invents a color of its own. */
  color: string;
  /** Glyph center. Required when `wrap` is false (chart embeds the
   *  primitives directly at real plot coordinates). Ignored when
   *  `wrap` is true — the wrapped <svg> centers at size/2. */
  cx?: number;
  cy?: number;
  /** Alert markers dim to 0.55 when acknowledged; default 1. */
  opacity?: number;
  /** Legend mode: wrap the primitives in a self-contained
   *  `<svg viewBox="0 0 size size">` centered at (size/2, size/2). */
  wrap?: boolean;
  /** Wrapped viewBox size (px). Default 16. */
  size?: number;
  /** Cycle 49: override the `reading`-dot radius (default 2.4).
   *  Applies ONLY to `kind === "reading"` in non-`wrap` mode — the
   *  chart bumps this to ~5 when zoomed (mobile pannable slice) so
   *  the sparser dots are comfortably tappable. Every other kind
   *  ignores it (carbs/insulin/other/alert keep their cycle-44
   *  fixed geometry so the legend stays pixel-faithful), and
   *  wrapped/legend mode is unaffected. */
  readingRadius?: number;
  /** Cycle 65: x-axis stretch correction factor. The HistoryChart
   *  SVG uses `preserveAspectRatio="none"` so its viewBox X axis
   *  stretches to whatever container width the chart sits in. A
   *  user-space `<circle>` therefore paints as an ellipse. Passing
   *  `xCorrect = VIEW_W / pxWidth` shrinks every shape's horizontal
   *  extent inversely to the stretch so dots paint as true circles,
   *  the carbs triangle keeps its proportions, and the insulin /
   *  other / alert markers stay round. Legend (wrap) mode uses a
   *  square viewBox + uniform scale so it ignores this value — pass
   *  it or don't, no effect. */
  xCorrect?: number;
  /** 0.65.6: y-axis stretch correction factor. The cycle-65 fix
   *  assumed Y mapped 1:1 because HistoryChart passes `height={H}`
   *  on the SVG, but in practice the parent's
   *  `.chartContainer > * { flex: 1 }` rule (Now-screen + kiosk
   *  History fill) stretches the wrap to flex-fill while the SVG's
   *  viewBox y-axis stays at H — so the SVG actually paints taller
   *  than its viewBox y-axis and dots became vertically-tall ovals
   *  even after the x-correction. Pass `yCorrect = H / pxHeight` to
   *  shrink every shape's vertical extent inversely to that stretch.
   *  Wrapped legend mode ignores this. */
  yCorrect?: number;
}

function primitives(
  kind: MarkerKind,
  cx: number,
  cy: number,
  color: string,
  opacity: number,
  readingRadius = 2.4,
  xCorrect = 1,
  yCorrect = 1,
) {
  // Cycle 65 + 0.65.6: render circles as <ellipse> with rx/ry
  // shrunk by xCorrect/yCorrect so they paint as true circles in
  // the chart's stretched user space (the SVG's viewBox can stretch
  // independently on either axis depending on parent flex layout).
  // For xCorrect === 1 && yCorrect === 1 (legend/wrap mode, or a
  // chart whose pxWidth === VIEW_W && pxHeight === H exactly) the
  // math collapses to rx === ry === baseR and behavior is byte-
  // identical to cycle 44.
  switch (kind) {
    case "carbs":
      // Symmetric isoceles: scale horizontal half-base by xCorrect
      // and the vertical apex/base offsets by yCorrect so the
      // triangle keeps its proportions on either axis stretch.
      return (
        <path
          d={`M ${cx} ${cy - 4 * yCorrect} L ${cx + 5 * xCorrect} ${
            cy + 4 * yCorrect
          } L ${cx - 5 * xCorrect} ${cy + 4 * yCorrect} Z`}
          fill={color}
          opacity={opacity}
        />
      );
    case "insulin":
      return (
        <ellipse
          cx={cx}
          cy={cy}
          rx={4 * xCorrect}
          ry={4 * yCorrect}
          fill={color}
          opacity={opacity}
        />
      );
    case "other":
      return (
        <ellipse
          cx={cx}
          cy={cy}
          rx={3 * xCorrect}
          ry={3 * yCorrect}
          fill={color}
          opacity={opacity}
        />
      );
    case "alert":
      return (
        <ellipse
          cx={cx}
          cy={cy}
          rx={5 * xCorrect}
          ry={5 * yCorrect}
          fill={color}
          stroke="var(--bg)"
          strokeWidth={1.5}
          opacity={opacity}
        />
      );
    case "reading":
    default:
      return (
        <ellipse
          cx={cx}
          cy={cy}
          rx={readingRadius * xCorrect}
          ry={readingRadius * yCorrect}
          fill={color}
          opacity={opacity}
        />
      );
  }
}

export function MarkerGlyph({
  kind,
  color,
  cx,
  cy,
  opacity = 1,
  wrap = false,
  size = 16,
  readingRadius,
  xCorrect = 1,
  yCorrect = 1,
}: Props) {
  if (wrap) {
    // Legend/wrapped mode is byte-unchanged (cycle 44 fidelity):
    // `readingRadius`, `xCorrect`, and `yCorrect` are intentionally
    // NOT threaded here — the wrapped <svg> uses a square viewBox
    // so the legend glyphs stay pixel-faithful to the cycle-44 spec.
    const c = size / 2;
    return (
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        style={{ display: "block", flexShrink: 0 }}
        aria-hidden="true"
      >
        {primitives(kind, c, c, color, opacity)}
      </svg>
    );
  }
  // Embedded in the chart's <svg> at real plot coordinates.
  // Cycle 49: `readingRadius` (when set) overrides the reading-dot
  // radius. Cycle 65 + 0.65.6: `xCorrect` + `yCorrect` correct for
  // the chart's non-uniform stretch on EITHER axis so every shape
  // paints as a true circle / proportional triangle regardless of
  // container dims.
  return (
    <Fragment>
      {primitives(
        kind,
        cx ?? 0,
        cy ?? 0,
        color,
        opacity,
        readingRadius,
        xCorrect,
        yCorrect,
      )}
    </Fragment>
  );
}
